import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, MessageCircle, Phone, ArrowRight, Clock } from "lucide-react";
import { COMPANY } from "../content";
import usePageMeta from "../hooks/usePageMeta";
import { track } from "../lib/analytics";

export default function ThankYou() {
  usePageMeta({
    title: "Thank You | Time Furniture",
    description:
      "Thank you for your enquiry. Our project team will be in touch shortly. Meanwhile, message us on WhatsApp or view our completed projects.",
    canonical: "https://timefurniture.ae/thank-you",
  });

  useEffect(() => {
    track.thankYouView({ source: "contact_form" });
    // Keep this page out of the index — it is a post-conversion confirmation.
    let robots = document.head.querySelector('meta[name="robots"]');
    const hadRobots = !!robots;
    const prev = robots ? robots.getAttribute("content") : null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, follow");
    return () => {
      if (hadRobots && prev != null) robots.setAttribute("content", prev);
      else if (robots && robots.parentNode) robots.parentNode.removeChild(robots);
    };
  }, []);

  const waHref = `https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappContactText}`;

  return (
    <div data-testid="thank-you-page" className="bg-[#FAF5EC] min-h-[70vh]">
      <section className="max-w-[900px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#3F6E2C]/10 text-[#3F6E2C] mb-8">
          <CheckCircle2 size={30} strokeWidth={1.8} />
        </div>
        <p className="eyebrow mb-5">Enquiry received</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-[#1C110C]">
          Thank you — we've got your request.
        </h1>
        <p className="mt-6 max-w-2xl text-[#7A6B5F] leading-relaxed">
          A member of our project team will contact you within one business day to understand your
          scope and arrange a site visit. For anything urgent, reach us directly below.
        </p>

        {/* What happens next */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#6E1F2C]/10 border border-[#6E1F2C]/10">
          {[
            { n: "01", t: "We review your enquiry", d: "Our team reads your project details and prepares relevant questions." },
            { n: "02", t: "We call you back", d: "Expect a call or WhatsApp within one business day to align on scope." },
            { n: "03", t: "We book a site visit", d: "A specialist visits to measure, advise and prepare an accurate proposal." },
          ].map((s) => (
            <div key={s.n} className="bg-[#FAF5EC] p-6">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">{s.n}</p>
              <p className="font-serif-display text-lg mt-2 text-[#1C110C]">{s.t}</p>
              <p className="mt-2 text-xs text-[#7A6B5F] leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>

        {/* Direct actions */}
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            data-testid="thank-you-whatsapp"
            className="inline-flex items-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors"
          >
            <MessageCircle size={16} /> Message on WhatsApp
          </a>
          <a
            href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
            data-testid="thank-you-phone"
            onClick={() => track.phoneClick("thank_you")}
            className="inline-flex items-center gap-3 border border-[#6E1F2C]/30 text-[#6E1F2C] px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:border-[#6E1F2C] transition-colors"
          >
            <Phone size={16} /> {COMPANY.phone}
          </a>
        </div>

        <p className="mt-8 inline-flex items-center gap-2 text-xs text-[#7A6B5F]">
          <Clock size={13} className="text-[#6E1F2C]" /> Mon – Sat · 9am – 7pm (GST)
        </p>

        <div className="mt-14 pt-10 border-t border-[#6E1F2C]/15">
          <Link
            to="/portfolio"
            data-testid="thank-you-portfolio"
            onClick={() => track.portfolioView({ cta_location: "thank_you" })}
            className="inline-flex items-center gap-3 text-[12px] tracking-[0.28em] uppercase text-[#6E1F2C] border-b border-[#6E1F2C] pb-1 hover:gap-5 transition-all"
          >
            View completed projects <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}
